// C:\Users\abdul\Downloads\Onebox\onebox\src\components\Testimonials.jsx
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaStar, FaUpload, FaTimes, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaQuoteLeft } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import './Testimonials.css';
import leafImage from '../assets/images/small-leaf.png';


const Testimonials = ({ tenant, data }) => {
  console.log('Testimonials received:', { tenant, data });

  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    feedback: '',
    experience: 'excellent',
    recommend: 'yes',
    waitingTime: '',
    foodQuality: '',
    foodFreshness: '',
    serviceQuality: '',
    ambiance: '',
    valueForMoney: '',
    cleanliness: ''
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const label = (data && data.label) ? String(data.label) : 'Testimonials';
  const title = (data && data.title) ? String(data.title) : 'What our client are saying';
  const primaryColor = (tenant && tenant.primary_color) ? tenant.primary_color : '#FF9F0D';

  const loadTestimonials = useCallback(async () => {
    if (!tenant || !tenant.id) return;

    try {
      setLoading(true);
      const { data: testimonialsData, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('is_approved', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;

      const mappedTestimonials = (testimonialsData || []).map(t => ({
        id: t.id,
        name: t.customer_name,
        email: t.customer_email,
        role: t.customer_role || 'Customer',
        rating: t.rating,
        feedback: t.feedback,
        photo_url: t.customer_image_url,
        images: t.food_images || [],
        created_at: t.created_at
      }));

      setTestimonials(mappedTestimonials);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      setTestimonials([
        {
          id: 1,
          name: 'Alamin Hasan',
          role: 'Food Blogger',
          rating: 5,
          feedback: 'Amazing food quality and excellent service! The ambiance is perfect and every dish exceeded my expectations.',
          photo_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80'
        },
        {
          id: 2,
          name: 'Sarah Johnson',
          role: 'Regular Customer',
          rating: 5,
          feedback: 'Best restaurant in town! The staff is friendly and the food is always fresh and delicious.',
          photo_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80'
        },
        {
          id: 3,
          name: 'Michael Chen',
          role: 'Food Critic',
          rating: 5,
          feedback: 'Exceptional culinary experience. The chef\'s attention to detail is remarkable. Highly recommended!',
          photo_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80'
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    if (tenant && tenant.id) {
      loadTestimonials();
    }
  }, [tenant, loadTestimonials]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 5MB.`);
        return false;
      }
      return true;
    });
    const newImages = validFiles.map(file => ({
      id: Date.now() + Math.random(),
      url: URL.createObjectURL(file),
      file: file
    }));
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (id) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const uploadImagesToStorage = async () => {
    const uploadedUrls = [];
    for (const img of uploadedImages) {
      try {
        const fileName = `${tenant.id}/${Date.now()}_${img.file.name}`;
        const { error } = await supabase.storage
          .from('testimonials')
          .upload(fileName, img.file, { cacheControl: '3600', upsert: false });
        if (error) throw error;
        const { data: publicUrlData } = supabase.storage
          .from('testimonials')
          .getPublicUrl(fileName);
        uploadedUrls.push(publicUrlData.publicUrl);
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreeTerms) { alert('Please agree to the terms and conditions'); return; }
    if (rating === 0) { alert('Please provide a rating'); return; }

    setSubmitting(true);
    try {
      const imageUrls = await uploadImagesToStorage();
      const testimonialData = {
        tenant_id: tenant.id,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_role: 'Customer',
        customer_image_url: 'https://ui-avatars.com/api/?name=' + encodeURIComponent(formData.name) + '&background=FF9F0D&color=fff&size=200',
        rating: rating,
        feedback: formData.feedback,
        food_images: imageUrls,
        detailed_feedback: {
          waitingTime: formData.waitingTime,
          foodQuality: formData.foodQuality,
          foodFreshness: formData.foodFreshness,
          serviceQuality: formData.serviceQuality,
          ambiance: formData.ambiance,
          valueForMoney: formData.valueForMoney,
          cleanliness: formData.cleanliness,
          recommend: formData.recommend
        },
        is_approved: false,
        source: 'website',
        created_at: new Date().toISOString()
      };

      const { error } = await supabase.from('testimonials').insert([testimonialData]).select();
      if (error) throw error;

      setFormData({
        name: '', email: '', feedback: '', experience: 'excellent', recommend: 'yes',
        waitingTime: '', foodQuality: '', foodFreshness: '', serviceQuality: '',
        ambiance: '', valueForMoney: '', cleanliness: ''
      });
      setRating(0);
      setUploadedImages([]);
      setAgreeTerms(false);
      setShowModal(false);
      alert('Thank you for your feedback! Your review will be published after approval.');
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const shareOnSocial = (platform) => {
    const message = `Check out my experience at ${tenant?.business_name || 'this restaurant'}! ${rating} stars!`;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}&quote=${encodeURIComponent(message)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${window.location.href}`,
      instagram: `https://www.instagram.com/`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  // ── Modal rendered via Portal directly into document.body ──
  const modalContent = showModal ? ReactDOM.createPortal(
    <div className="feedback-modal-overlay" onClick={() => setShowModal(false)}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setShowModal(false)}>
          <FaTimes />
        </button>

        <div className="modal-header">
          <h3 style={{ color: primaryColor }}>Share Your Feedback</h3>
          <p>Help us serve you better with your valuable feedback</p>
        </div>

        <form className="feedback-form" onSubmit={handleSubmit}>
          {/* Overall Rating */}
          <div className="form-group highlight-group">
            <label>Overall Experience *</label>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  className={`star ${star <= (hoverRating || rating) ? 'active' : ''}`}
                  style={{ color: star <= (hoverRating || rating) ? primaryColor : 'rgba(255, 255, 255, 0.15)' }}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                />
              ))}
              <span className="rating-text" style={{ color: primaryColor }}>
                {rating > 0 ? `${rating}/5` : 'Click to rate'}
              </span>
            </div>
          </div>

          {/* Name & Email */}
          <div className="form-row">
            <div className="form-group">
              <label>Your Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter your name" required />
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter your email" required />
            </div>
          </div>

          {/* Detailed Feedback */}
          <div className="feedback-options-section">
            <h4 className="section-title-left">Detailed Feedback</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Waiting Time *</label>
                <div className="custom-select">
                  <select name="waitingTime" value={formData.waitingTime} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="excellent">Excellent (Very Fast)</option>
                    <option value="good">Good (Reasonable)</option>
                    <option value="average">Average (A bit long)</option>
                    <option value="poor">Poor (Too long)</option>
                    <option value="no-answer">Prefer not to answer</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Food Quality *</label>
                <div className="custom-select">
                  <select name="foodQuality" value={formData.foodQuality} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="poor">Poor</option>
                    <option value="no-answer">Prefer not to answer</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Food Freshness *</label>
                <div className="custom-select">
                  <select name="foodFreshness" value={formData.foodFreshness} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="very-fresh">Very Fresh</option>
                    <option value="fresh">Fresh</option>
                    <option value="not-fresh">Not Fresh</option>
                    <option value="dont-know">Don't Know</option>
                    <option value="no-answer">Prefer not to answer</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Service Quality *</label>
                <div className="custom-select">
                  <select name="serviceQuality" value={formData.serviceQuality} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="poor">Poor</option>
                    <option value="no-answer">Prefer not to answer</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Ambiance *</label>
                <div className="custom-select">
                  <select name="ambiance" value={formData.ambiance} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="poor">Poor</option>
                    <option value="no-answer">Prefer not to answer</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Value for Money *</label>
                <div className="custom-select">
                  <select name="valueForMoney" value={formData.valueForMoney} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="poor">Poor</option>
                    <option value="no-answer">Prefer not to answer</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Cleanliness *</label>
                <div className="custom-select">
                  <select name="cleanliness" value={formData.cleanliness} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="poor">Poor</option>
                    <option value="no-answer">Prefer not to answer</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Would Recommend? *</label>
                <div className="custom-select">
                  <select name="recommend" value={formData.recommend} onChange={handleInputChange} required>
                    <option value="">Select</option>
                    <option value="yes">Yes, Definitely</option>
                    <option value="maybe">Maybe</option>
                    <option value="no">No</option>
                    <option value="dont-know">Don't Know</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Comments */}
          <div className="form-group">
            <label>Additional Comments *</label>
            <textarea name="feedback" value={formData.feedback} onChange={handleInputChange} placeholder="Tell us more about your experience..." rows="4" required />
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label>Upload Photos (Optional)</label>
            <p className="field-hint">Share photos of your dining experience</p>
            <div className="image-upload-area">
              <input type="file" id="image-upload" accept="image/*" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
              <label htmlFor="image-upload" className="upload-label">
                <FaUpload />
                <span>Click to upload images</span>
                <span className="upload-hint">JPG, PNG up to 5MB</span>
              </label>
            </div>
            {uploadedImages.length > 0 && (
              <div className="uploaded-images">
                {uploadedImages.map((img) => (
                  <div key={img.id} className="uploaded-image">
                    <img src={img.url} alt="Upload preview" />
                    <button type="button" className="remove-image" onClick={() => removeImage(img.id)}>
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <div className="form-group disclaimer-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} required />
              <span className="disclaimer-text">
                I agree that my feedback and uploaded images may be used for marketing and advertising purposes.
                By submitting, I consent to the restaurant showcasing my review on their website, social media,
                and promotional materials.
              </span>
            </label>
          </div>

          {/* Social Share */}
          <div className="social-share-section">
            <label>Share Your Experience</label>
            <div className="social-share-buttons">
              <button type="button" className="social-share-btn facebook" onClick={() => shareOnSocial('facebook')}><FaFacebookF /></button>
              <button type="button" className="social-share-btn twitter" onClick={() => shareOnSocial('twitter')}><FaTwitter /></button>
              <button type="button" className="social-share-btn instagram" onClick={() => shareOnSocial('instagram')}><FaInstagram /></button>
              <button type="button" className="social-share-btn linkedin" onClick={() => shareOnSocial('linkedin')}><FaLinkedinIn /></button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="submit-feedback-btn"
            disabled={submitting}
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
              boxShadow: `0 12px 40px ${primaryColor}99`
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      </div>
    </div>,
    document.body  // ← Portal renders directly into <body>, bypassing all parent constraints
  ) : null;

  return (
    <section className="testimonials-section">
      {/* Decorative Leaves */}
      <div className="testimonial-leaf testimonial-leaf-left">
        <img src={leafImage} alt="" className="leaf-img" />
      </div>
      <div className="testimonial-leaf testimonial-leaf-right">
        <img src={leafImage} alt="" className="leaf-img" />
      </div>

      <div className="testimonials-container">
        {/* Section Header */}
        <div className="testimonials-header">
          <span className="testimonials-label" style={{ color: primaryColor }}>{label}</span>
          <h2 className="testimonials-title">{title}</h2>
        </div>

        {/* Testimonials Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.6)' }}>
            Loading testimonials...
          </div>
        ) : testimonials.length > 0 ? (
          <div className="testimonials-grid-compact">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="review-card-compact">
                <FaQuoteLeft className="quote-icon-compact" style={{ color: primaryColor }} />
                <div className="review-rating">
                  {[...Array(testimonial.rating || 5)].map((_, idx) => (
                    <FaStar key={idx} className="review-star" style={{ color: primaryColor }} />
                  ))}
                </div>
                <p className="review-text-compact">{testimonial.feedback}</p>
                <div className="review-author-compact">
                  <img
                    src={testimonial.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=FF9F0D&color=fff&size=100`}
                    alt={testimonial.name}
                    className="review-author-image"
                    style={{ borderColor: primaryColor }}
                  />
                  <div className="review-author-info">
                    <h4 className="review-author-name">{testimonial.name}</h4>
                    <p className="review-author-role">{testimonial.role || 'Customer'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.6)' }}>
            No testimonials yet. Be the first to share your experience!
          </div>
        )}

        {/* Feedback Button */}
        <div className="feedback-button-wrapper">
          <button
            className="feedback-trigger-button"
            onClick={() => setShowModal(true)}
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
              boxShadow: `0 10px 40px ${primaryColor}80`
            }}
          >
            Share Your Experience
          </button>
        </div>
      </div>

      {/* Portal Modal — mounted directly on document.body */}
      {modalContent}
    </section>
  );
};

export default Testimonials;
